import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { useQuery, useApolloClient, useMutation } from "@apollo/client";
import { cn } from "@/lib/utils";
import { CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import {
  StyledTableShell,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  EmptyState,
  getStatusAccentColor,
} from "@/components/ui/styled-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, Column } from "@/components/ui/data-table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader } from "@/components/ui/page-header";
import { AudiencePill, StatusBadge } from "@/components/ui/table-components";
import { RolesTable } from "./RolesTable";
import { TableSortControls } from "@/components/ui/table-sort-controls";
import RoleDetailModal from "./RoleDetailModal";
import ProjectRoleDetailModal from "./ProjectRoleDetailModal";
import RoleDeleteModal from "./RoleDeleteModal";
import {
  DesktopContainer,
  MobileContainer,
  PaginationContainer,
  TabCardContainer,
} from "../../sharedStyles";
import {
  GET_ROLES,
  GET_IMPORTED_JOBS,
  GET_ROLE_BY_ID,
} from "../../GraphQL/roles/queries";
import { GET_LOCALITIES } from "../../GraphQL/settings/queries";
import { IMPORT_ROLE } from "../../GraphQL/roles/mutations";
import { Job, JobData, LocalityData } from "../../interfaces";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useParams, useNavigate } from "react-router-dom";
import { Download, Pencil, Trash2, Plus } from "lucide-react";
import { ProfileContext } from "../../App";
import ChevronTableHeader from "../Icons/ChevronTableHeader";
import RolesMobileView from "./RolesMobileView";
import { RolesTableSkeleton } from "./RolesTableSkeleton";
import CustomPagination, {
  CustomPaginationType,
} from "../Global/CustomPagination";
import Notification from "../Notification";
import {
  EToastPosition,
  EToastColorStatus,
  IToastData,
} from "../../interfaces";
import config from "../../config";
import guide from "../../assets/role_relay_user_guide.pdf";

// Styled component still used for non-migrated tabs (imported/project roles)
const RolesHeaderTitle = ({ children }: { children: React.ReactNode }) => (
  <div className="cursor-pointer flex justify-between">
    {children}
  </div>
);

const statusLabels = {
  PLATFORM_INCLUDED: "Pending Opt-in",
  SHARED: "Shared",
  STOPPED_SHARING: "Draft",
  REMOVED: "Removed",
  UNKNOWN: "Unknown",
};

const roleSortOptions = [
  { value: "clientName", label: "Client" },
  { value: "title", label: "Role Title" },
  { value: "industry", label: "Industry" },
  { value: "location.displayName", label: "Location" },
  { value: "workType", label: "Type" },
  { value: "audience", label: "Audience" },
  { value: "matches", label: "Matches" },
  { value: "status", label: "Status" },
];

interface Props {
  user: any;
}

const Roles = ({ user }: Props) => {
  const navigate = useNavigate();
  let atsServer = "au1";
  if (
    user?.username?.includes("firefly") ||
    user?.username?.includes("worktrybe")
  )
    atsServer = "au3";
  if (user?.username?.includes("lloyd") || user?.username?.includes("candidex"))
    atsServer = "au5";

  const profile = useContext(ProfileContext);
  const hasProject =
    profile.userProfile?.company?.settings?.features?.projects || false;
  const myRolesPaginationRef = useRef<CustomPaginationType>(null);
  const showImportedFlag = useFeatureFlagEnabled(
    config?.postHog?.featureFlags?.showImported
  );
  const [selectedRole, setSelectedRole] = useState<Job | null>(null);
  const [showRoleDetailModal, setShowRoleDetailModal] = useState(false);
  const [showRoleDeleteModal, setShowDeleteModal] = useState(false);
  const [showProjectRoleDetailModal, setShowProjectRoleDetailModal] = useState(false);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [nonATS, setNonATS] = useState(profile.connections?.totalCount === 0);

  const [activeTab, setActiveTab] = useState("roles");
  //const [selectMyRoleId, setSelectMyRoleId] = useState("");
  const [orderSortInput, setOrderSortInput] = useState<any[]>([
    { updatedAt: "DESC" },
  ]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [toastData, setToastData] = useState<IToastData>({
    show: false,
    color: EToastColorStatus.SUCCESS,
    position: EToastPosition.BOTTOM_END,
    message: "",
  });

  const getJobKey = (job: Job, index: number) => {
    if (job?.id != null && job.id !== 0) {
      return `job-${job.id}`;
    }
    if (job?.externalId) {
      return `job-ext-${job.externalId}`;
    }
    return `job-idx-${index}`;
  };

  useEffect(() => {
    setNonATS(profile.connections?.totalCount === 0);
  }, [profile]);

  const { loading, error, data } = useQuery<JobData>(GET_ROLES, {
    variables: { skip: 0, take: 25, order: { updatedAt: "DESC" } },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  const { data: dataLocalities } = useQuery<LocalityData>(GET_LOCALITIES);
  const localities = dataLocalities?.localities?.items
    ? [...dataLocalities?.localities?.items]
    : [];
  const locationName = (x: string): string[] => {
    return localities.map((locality) =>
      x === locality?.name ? `${locality?.name} (${locality?.state})` : ""
    );
  };

  const {
    loading: importedJobsLoading,
    error: importedJobsError,
    data: importedJobsData,
  } = useQuery(GET_IMPORTED_JOBS);

  // const { data: dataByRoleId } = useQuery(GET_ROLE_BY_ID, {
  //   variables: { id: selectMyRoleId },
  // });

  const [importRole] = useMutation(IMPORT_ROLE, {
    onCompleted(dataImported) {
      setActiveTab("roles");
      const newRoleId = dataImported?.importJob?.id;
      navigate(`/my-roles/${newRoleId}`);
      
      // We pass a "skeleton" role containing just the ID. 
      // The modal's useLazyQuery will see this ID, wake up, and fetch the full data!
      setSelectedRole({ id: newRoleId } as Job); 
      setShowRoleDetailModal(true);
    },
    onError(error: any) {
      const errorMessage = error?.networkError?.result?.errors[0]?.message;
      setToastData({
        show: true,
        color: EToastColorStatus.FAILED,
        position: EToastPosition.BOTTOM_END,
        message: `Failed, ${errorMessage}`,
      });
    },
  });

  // useEffect(() => {
  //   setSelectedRole(dataByRoleId?.jobs?.items[0]);
  // }, [dataByRoleId]);

  useEffect(() => {
    setRolesData(data?.jobs?.items || []);
    setHasNextPage(data?.jobs?.pageInfo?.hasNextPage || false);
  }, [data]);

  const client = useApolloClient();
  const refreshData = () =>
    client.refetchQueries({
      include: [GET_ROLES],
    });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSortFieldChange = (field: string) => {
    setSortField(field);
    if (!sortField || sortField !== field) {
      setSortDirection('asc');
    }
  };

  const toggleSortDirection = () => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const getSortedData = <T extends Record<string, any>>(data: T[]): T[] => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle nested properties like location.displayName
      if (sortField.includes('.')) {
        const [parent, child] = sortField.split('.');
        aVal = a[parent]?.[child];
        bVal = b[parent]?.[child];
      }

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1;
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1;

      // Numeric comparison for numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // String comparison
      const comparison = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const [rolesData, setRolesData] = useState<Job[]>(data?.jobs?.items || []);
  const visibleRoles = rolesData.filter(job => job.clientName.indexOf('[VMS]') === -1);
  const filteredRoles = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return visibleRoles;
    }

    return visibleRoles.filter((role) => {
      const haystack = [
        role.clientName,
        role.title,
        role.industry,
        role.location?.displayName,
        role.externalId,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [visibleRoles, searchTerm]);

  const runPagination = (skip = 0, take = 25) => {
    const getRolesData = async () => {
      const { data } = await client.query({
        query: GET_ROLES,
        variables: { skip, take, order: { updatedAt: "DESC" } },
      });
      setRolesData(data?.jobs?.items);
      myRolesPaginationRef.current?.setPagination({
        ...data?.jobs?.pageInfo,
        skip,
        take,
        totalCount: data?.jobs?.totalCount,
      });
    };
    getRolesData();
  };

  /**
   * Logic for deeplinked roles
   */
  const [routeRoleId, setRouteRoleId] = useState<number | null>(null);
  const { roleId } = useParams<{ roleId: string }>();

  const [modalKey, setModalKey] = useState(0);

  const handleNewRole = () => {
    setModalKey(modalKey + 1);
    setSelectedRole(null);
    setShowRoleDetailModal(true);
  };

  const handleNewProjectRole = () => {
    setModalKey(modalKey + 1);
    setSelectedRole(null);
    setShowProjectRoleDetailModal(true);
  };

  useEffect(() => {
    roleId ? setRouteRoleId(Number.parseInt(roleId)) : setRouteRoleId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    runPagination();
  }, [data?.jobs?.items]);

  useEffect(() => {
    setRolesData(rolesData);
  }, [rolesData]);

useEffect(() => {
    if (routeRoleId !== null && rolesData.length > 0 && !showRoleDetailModal) {
      const role = rolesData.find((r) => r.id === routeRoleId);
      if (role) {
        setSelectedRole(role);
        setShowRoleDetailModal(true);
      }
    }
  }, [routeRoleId, rolesData]); // Removed showRoleDetailModal from deps to avoid infinite loops

  //TODO: RR-10
  // if (loading || error) return null;

  const handleShowModalDelete = (id: number) => {
    setShowDeleteModal(true);
    setDeleteId(id);
  };

  const handleRoleDetails = (data: Job) => {
    navigate(`/my-roles/${data?.id}`);
    setSelectedRole(data);
    setShowRoleDetailModal(true);
  };

  const handleProjectRoleDetails = (data: Job) => {
    navigate(`/my-roles/${data?.id}`);
    setSelectedRole(data);
    setShowProjectRoleDetailModal(true);
  };

  const handleImportRoleModal = (data: Job) => {
    const string = data?.externalId;
    const roleId = string.replace(/\D/g, "");
    importRole({
      variables: {
        jobId: Number(roleId),
      },
    });
  };

  const onHandleActiveTab = (activeTab: string | null) => {
    if (activeTab) {
      setActiveTab(activeTab);
    }
  };

  const handleShowToast = (
    color: EToastColorStatus,
    position: EToastPosition,
    message: string
  ) => {
    setToastData({ show: true, color, position, message });
  };

  const importedRolesColumns = useMemo<Column<Job>[]>(() => [
    {
      id: "clientName",
      header: "Client",
      cell: (job) => <span className="font-medium text-slate-900 dark:text-slate-100">{job?.clientName || "-"}</span>,
    },
    {
      id: "title",
      header: "Job Title",
      cell: (job) => <span className="font-medium text-slate-900 dark:text-slate-100">{job?.title || "-"}</span>,
    },
    {
      id: "industry",
      header: "Industry",
      cell: (job) => <span className="text-slate-700 dark:text-slate-300">{job?.industry || "-"}</span>,
    },
    {
      id: "location.displayName",
      header: "Location",
      cell: (job) => <span className="text-slate-700 dark:text-slate-300">{locationName(job?.location?.displayName) ?? "-"}</span>,
    },
    {
      id: "status",
      header: "Status",
      className: "text-center",
      cell: (job) => <StatusBadge status={statusLabels[job?.status] || 'Unknown'} />,
    },
    {
      id: "actions",
      header: "Actions",
      className: "text-center",
      width: "120px",
      cell: (job) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              handleImportRoleModal(job);
            }}
            className="hover:bg-teal-50 hover:text-teal-700 dark:hover:bg-teal-900/30 dark:hover:text-teal-300"
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              handleShowModalDelete(job?.id);
            }}
            className="hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-900/30 dark:hover:text-rose-300"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ], [locationName]);

  return (
    <div className="max-w-[1920px] mx-auto px-4">
      <RoleDetailModal
        key={modalKey}
        isNonATS={nonATS}
        show={showRoleDetailModal}
        role={selectedRole}
        reloadRoles={refreshData}
        onHide={() => {
          window.history.replaceState(null, "", `/my-roles/`);
          setRouteRoleId(null);
          setShowRoleDetailModal(false);
        }}
        atsServer={atsServer}
        onCreateToast={(
          color: EToastColorStatus,
          position: EToastPosition,
          message: string
        ) => handleShowToast(color, position, message)}
        onShowDelete={(data: boolean) => setShowDeleteModal(data)}
        onSetDeleteId={(data: number) => setDeleteId(data)}
      />
      <ProjectRoleDetailModal
        key={modalKey}
        isNonATS={true}
        show={showProjectRoleDetailModal}
        role={selectedRole}
        reloadRoles={refreshData}
        onHide={() => {
          window.history.replaceState(null, "", `/my-roles/`);
          setRouteRoleId(null);
          setShowProjectRoleDetailModal(false);
        }}
        atsServer={atsServer}
        onCreateToast={(
          color: EToastColorStatus,
          position: EToastPosition,
          message: string
        ) => handleShowToast(color, position, message)}
        onShowDelete={(data: boolean) => setShowDeleteModal(data)}
        onSetDeleteId={(data: number) => setDeleteId(data)}
      />
      <RoleDeleteModal
        id={deleteId}
        show={showRoleDeleteModal}
        onHideModal={() => setShowDeleteModal(false)}
        reloadRoles={refreshData}
        onCreateToast={(
          color: EToastColorStatus,
          position: EToastPosition,
          message: string
        ) => handleShowToast(color, position, message)}
      />
      <PageHeader title="My Roles" subtitle="Manage your roles" />
      <Tabs value={activeTab} onValueChange={(value) => onHandleActiveTab(value)}>
        <TabsList>
          <TabsTrigger value="roles">Roles</TabsTrigger>
          {showImportedFlag && <TabsTrigger value="imported-roles">Imported Roles</TabsTrigger>}
          {hasProject && <TabsTrigger value="project_roles">Project Roles</TabsTrigger>}
        </TabsList>
        <TabsContent value="roles">
          <div className="space-y-4">
            {/* Header row with search/sort and action button */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <TableSortControls
                sortField={sortField || "clientName"}
                sortDirection={sortDirection}
                options={roleSortOptions}
                onSortFieldChange={handleSortFieldChange}
                onSortDirectionToggle={toggleSortDirection}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search by client, role, industry, location..."
              />
              <Button
                onClick={handleNewRole}
                className={cn(
                  "h-10 px-5 shrink-0",
                  "bg-primary hover:bg-primary/90",
                  "text-primary-foreground",
                  "shadow-[0_2px_8px_rgba(10,95,111,0.25)]",
                  "hover:shadow-[0_4px_12px_rgba(10,95,111,0.35)]",
                  "transition-all duration-200 ease-out",
                  "active:scale-[0.97] active:shadow-[0_1px_4px_rgba(10,95,111,0.2)]",
                  "font-medium"
                )}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Role
              </Button>
            </div>

            {loading && (
              <DesktopContainer>
                <RolesTableSkeleton rows={5} />
              </DesktopContainer>
            )}

            {!loading && error && (
              <div className="text-center p-4 bg-rose-50 dark:bg-rose-900/20 rounded-lg border border-rose-200 dark:border-rose-800">
                <p className="text-rose-700 dark:text-rose-300">
                  Unable to load roles at this time - please try again soon
                </p>
              </div>
            )}

            {/* Desktop Table */}
            {!loading && (
              <DesktopContainer>
                <RolesTable
                  roles={getSortedData(filteredRoles)}
                  locationName={locationName}
                  onRoleClick={handleRoleDetails}
                  onEditRole={handleRoleDetails}
                  onDeleteRole={handleShowModalDelete}
                  sortBy={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  isLoading={false}
                />
              </DesktopContainer>
            )}

            {/* Mobile View */}
            {!loading && (
              <MobileContainer>
                <RolesMobileView
                  onShowRoleModal={() => setShowRoleDetailModal(true)}
                  onSelectRole={(data) => setSelectedRole(data)}
                  onSortingRolesData={(data) => handleSort(data)}
                  rolesData={getSortedData(filteredRoles)}
                  locationName={locationName}
                  handleShowModalDelete={handleShowModalDelete}
                />
              </MobileContainer>
            )}

            {/* Pagination */}
            {hasNextPage && (
              <div className="flex justify-end">
                <CustomPagination
                  ref={myRolesPaginationRef}
                  onPaginationChange={(args) =>
                    runPagination(args.skip, args.take)
                  }
                />
              </div>
            )}

            <p className="text-sm text-slate-600 dark:text-slate-400">
              <a
                href={guide}
                target="_blank"
                rel="noreferrer"
                className="hover:text-teal-600 dark:hover:text-teal-400 transition-colors underline"
              >
                Click here to view the User Guide.
              </a>
            </p>
          </div>
        </TabsContent>
        {showImportedFlag && (
          <TabsContent value="imported-roles">
            <div className="space-y-4">
              {importedJobsError && (
                <div className="text-center p-4 bg-rose-50 dark:bg-rose-900/20 rounded-lg border border-rose-200 dark:border-rose-800">
                  <p className="text-rose-700 dark:text-rose-300">
                    Unable to load imported roles at this time - please try again soon
                  </p>
                </div>
              )}
              <DataTable
                data={importedJobsData?.importedJobs?.items || []}
                columns={importedRolesColumns}
                getRowId={getJobKey}
                getRowAccentColor={(job) => getStatusAccentColor(job.status || "")}
                isLoading={importedJobsLoading}
                emptyMessage="No imported roles yet."
              />
            </div>
          </TabsContent>
        )}
        {hasProject && (
          <TabsContent value="project_roles">
            <StyledTableShell>
              <CardContent>
                {loading && (
                  <div style={{ marginBottom: "8px" }}>Loading...</div>
                )}
                {!loading && error && (
                  <div style={{ marginBottom: "8px" }}>
                    Unable to load roles at this time - please try again soon
                  </div>
                )}
                <DesktopContainer>
                  <StyledTable hidden={loading}>
                    <TableHeader>
                      <TableRow>
                        <StyledTableHead onClick={() => handleSort("clientName")}>
                          <RolesHeaderTitle>
                            <div>Client</div>
                            <div>
                              {sortField === "clientName" && (
                                <ChevronTableHeader
                                  ascending={sortDirection === "asc"}
                                />
                              )}
                            </div>
                          </RolesHeaderTitle>
                        </StyledTableHead>
                        <StyledTableHead onClick={() => handleSort("title")}>
                          <RolesHeaderTitle>
                            <div>Job Title</div>
                            <div>
                              {sortField === "title" && (
                                <ChevronTableHeader
                                  ascending={sortDirection === "asc"}
                                />
                              )}
                            </div>
                          </RolesHeaderTitle>
                        </StyledTableHead>
                        <StyledTableHead onClick={() => handleSort("industry")}>
                          <RolesHeaderTitle>
                            <div>Industry</div>
                            <div>
                              {sortField === "industry" && (
                                <ChevronTableHeader
                                  ascending={sortDirection === "asc"}
                                />
                              )}
                            </div>
                          </RolesHeaderTitle>
                        </StyledTableHead>
                        <StyledTableHead onClick={() => handleSort("location.displayName")}>
                          <RolesHeaderTitle>
                            <div>Location</div>
                            <div>
                              {sortField === "location.displayName" && (
                                <ChevronTableHeader
                                  ascending={sortDirection === "asc"}
                                />
                              )}
                            </div>
                          </RolesHeaderTitle>
                        </StyledTableHead>
                        <StyledTableHead
                          className="text-center"
                          onClick={() => handleSort("workType")}
                        >
                          <RolesHeaderTitle>
                            <div>Type</div>
                            <div>
                              {sortField === "workType" && (
                                <ChevronTableHeader
                                  ascending={sortDirection === "asc"}
                                />
                              )}
                            </div>
                          </RolesHeaderTitle>
                        </StyledTableHead>
                        <StyledTableHead
                          className="text-center"
                          onClick={() => handleSort("audience")}
                        >
                          <RolesHeaderTitle>
                            <div>Audience</div>
                            <div>
                              {sortField === "audience" && (
                                <ChevronTableHeader
                                  ascending={sortDirection === "asc"}
                                />
                              )}
                            </div>
                          </RolesHeaderTitle>
                        </StyledTableHead>
                        <StyledTableHead
                          className="text-center"
                          onClick={() => handleSort("matches")}
                        >
                          <RolesHeaderTitle>
                            <div>Matches</div>
                            <div>
                              {sortField === "matches" && (
                                <ChevronTableHeader
                                  ascending={sortDirection === "asc"}
                                />
                              )}
                            </div>
                          </RolesHeaderTitle>
                        </StyledTableHead>
                        <StyledTableHead
                          className="text-center"
                          onClick={() => handleSort("status")}
                        >
                          <RolesHeaderTitle>
                            <div>Status</div>
                            <div>
                              {sortField === "status" && (
                                <ChevronTableHeader
                                  ascending={sortDirection === "asc"}
                                />
                              )}
                            </div>
                          </RolesHeaderTitle>
                        </StyledTableHead>
                        <StyledTableHead
                          className="text-center"
                          style={{ minWidth: "85px" }}
                        >
                          Actions
                        </StyledTableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rolesData.filter(job => job.clientName.indexOf('[VMS]') > -1).length === 0 && (
                        <TableRow>
                          <StyledTableCell colSpan={9}>
                            <EmptyState>No project roles available.</EmptyState>
                          </StyledTableCell>
                        </TableRow>
                      )}
                      {getSortedData(rolesData.filter(job => job.clientName.indexOf('[VMS]') > -1)).map((job, index) => (
                        <StyledTableRow
                          key={getJobKey(job, index)}
                          $accentColor={getStatusAccentColor(job.status || '')}
                          style={{ cursor: 'pointer' }}
                        >
                          <StyledTableCell onClick={() => handleProjectRoleDetails(job)}>
                            {job.clientName}
                          </StyledTableCell>
                          <StyledTableCell onClick={() => handleProjectRoleDetails(job)}>
                            {job.title}
                          </StyledTableCell>
                          <StyledTableCell onClick={() => handleProjectRoleDetails(job)}>
                            {job.industry || "-"}
                          </StyledTableCell>
                          <StyledTableCell onClick={() => handleProjectRoleDetails(job)}>
                            {locationName(job?.location?.displayName)}
                          </StyledTableCell>
                          <StyledTableCell
                            align="center"
                            onClick={() => handleProjectRoleDetails(job)}
                          >
                            <Badge variant="secondary">
                              {job.workType
                                ? job.workType === "TEMP"
                                  ? "CONTRACT/TEMP"
                                  : "PERMANENT"
                                : "-"}
                            </Badge>
                          </StyledTableCell>
                          <StyledTableCell
                            align="center"
                            onClick={() => handleProjectRoleDetails(job)}
                          >
                            <AudiencePill audience={job.audience} />
                          </StyledTableCell>
                          <StyledTableCell
                            align="center"
                            onClick={() => handleProjectRoleDetails(job)}
                          >
                            <Badge
                              variant={job.matches ? "default" : "secondary"}
                            >
                              {job.matches}
                            </Badge>
                          </StyledTableCell>
                          <StyledTableCell
                            align="center"
                            onClick={() => handleProjectRoleDetails(job)}
                          >
                            <StatusBadge status={statusLabels[job.status] as string || 'Unknown'} />
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleProjectRoleDetails(job)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>{" "}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleShowModalDelete(job?.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </StyledTable>
                </DesktopContainer>
                <MobileContainer>
                  <RolesMobileView
                    onShowRoleModal={() => setShowRoleDetailModal(true)}
                    onSelectRole={(data) => setSelectedRole(data)}
                    onSortingRolesData={(data) => handleSort(data)}
                    rolesData={rolesData.filter(job => job.clientName.indexOf('[VMS]') > -1)}
                    locationName={locationName}
                    handleShowModalDelete={handleShowModalDelete}
                  />
                </MobileContainer>
                <PaginationContainer>
                  <p>
                    <Button onClick={handleNewProjectRole}>Add Project Role</Button>
                  </p>
                  {hasNextPage && (
                    <CustomPagination
                      ref={myRolesPaginationRef}
                      onPaginationChange={(args) =>
                        runPagination(args.skip, args.take)
                      }
                    />
                  )}
                </PaginationContainer>
                <p>
                  <a href={guide} target="_blank" rel="noreferrer">
                    Click here to view the User Guide.
                  </a>
                </p>
              </CardContent>
            </StyledTableShell>
          </TabsContent>
        )}
      </Tabs>
      <Notification
        delay={3000}
        toastData={toastData}
        onClose={() => setToastData({ ...toastData, show: false })}
      />
    </div>
  );
};

export default Roles;
